import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { AnimatePresence, motion } from 'framer-motion';
import Headroom from 'react-headroom';
import cx from 'classnames';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';

import { useNavQuery } from '@querys/useNavQuery';
import { SideMenu } from './sideMenu';
import { NavModal } from './navModal';
import { SearchModal } from './searchModal';
import { useSiteContext, useSubscriptionOpen } from '../../lib/context';

import Logo from '@svg/logo-blue-new.svg';
import LogoWhite from '@svg/logo-white-new.svg';
import LogoGrey from '@svg/logo-grey.svg';
import Search from '@svg/search.svg';
import SearchWhite from '@svg/search-white.svg';
import SearchGrey from '@svg/search-grey.svg';
import Menu from '@svg/menu.svg';
import MenuWhite from '@svg/menu-white.svg';
import MenuGrey from '@svg/menu-grey.svg';
import Exit from '@svg/exit-white.svg';
import Music from '@svg/music.svg';

const musicVars = {
  initial: {
    width: '15.5%',
  },
  animate: {
    width: '40%',
    transition: {
      delay: 0.3,
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

const Header = ({ reverseHeader, unpinnedHeader, ticketingPage }) => {
  const breakpoints = useBreakpoint();

  const [isPinned, setIsPinned] = useState(false);
  const [sideMenuOpen, setSideMenuOpen] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const toggleOpen = () =>
    breakpoints.xl
      ? setSideMenuOpen((prev) => !prev)
      : setNavOpen((prev) => !prev);
  const { state } = useSiteContext();
  const { mainMenu } = useNavQuery();
  // Trigger re-render
  const [menuSwap, setMenuSwap] = useState(false);
  useEffect(() => {
    !breakpoints.lg && setMenuSwap((prev) => !prev);
  }, [navOpen, searchOpen]);

  //logo hover
  const [logoHover, setLogoHover] = useState(false);
  return (
    <>
      <Headroom
        style={{ zIndex: '50' }}
        onPin={() => setIsPinned(true)}
        onUnpin={() => setIsPinned(false)}
        onUnfix={() => setIsPinned(false)}
        disable={unpinnedHeader}>
        <div
          className={cx(
            'py-2 lg:pb-12 lg:pt-8 px-gutter lg:px-10 flex items-center lg:items-end justify-between transition-standard',
            {
              'backdrop-blur-2xl bg-sand bg-opacity-75':
                isPinned && !navOpen && !searchOpen,
              'text-grey': ticketingPage,
              'text-teal': !ticketingPage && (!reverseHeader || isPinned),
              'text-white': !ticketingPage && reverseHeader && !isPinned,
            },
          )}>
          <div
            onMouseEnter={() => setLogoHover(true)}
            onMouseLeave={() => setLogoHover(false)}
            className='relative'>
            <Link
              to='/'
              className={cx('transition-standard block', {
                'opacity-100': !logoHover,
                '!opacity-0': logoHover && !ticketingPage,
              })}>
              {navOpen || searchOpen ? (
                <LogoWhite className='max-w-[150px] lg:max-w-[250px] w-full' />
              ) : ticketingPage ? (
                <LogoGrey className='max-w-[150px] lg:max-w-[250px] w-full' />
              ) : reverseHeader && !isPinned ? (
                <LogoWhite className='max-w-[150px] lg:max-w-[250px] w-full' />
              ) : (
                <Logo className='max-w-[150px] lg:max-w-[250px] w-full' />
              )}
            </Link>

            {breakpoints.lg && !ticketingPage && (
              <motion.div
                variants={musicVars}
                initial='initial'
                animate={logoHover ? 'animate' : 'initial'}
                className='absolute top-0 left-0'>
                <Link to='/'>
                  <Music
                    className={cx('music', {
                      'music-reverse':
                        isPinned || !reverseHeader || ticketingPage,
                    })}
                  />
                </Link>
              </motion.div>
            )}
          </div>
          <div className='flex items-center gap-x-9'>
            {mainMenu?.map(({ page, linkText, maoriLinkText, _type }, i) => (
              <Link
                key={i}
                to={`/${page?.slug?.current}`}
                activeStyle={{ opacity: '60%' }}
                className='blockH8 hidden xl:block hover:opacity-60 transition-standard'>
                <span className='block'>{maoriLinkText}</span>
                <span className='block mt-1 font-bold'>{linkText}</span>
              </Link>
            ))}
            <div className='flex items-center'>
              <button
                className={cx(
                  'rounded-full w-12 lg:w-16 h-12 lg:h-16 bg-opacity-10 hover:bg-opacity-100 transition-standard',
                  {
                    '!bg-grey/10': ticketingPage,
                    'bg-white':
                      (reverseHeader && !isPinned) || navOpen || searchOpen,
                    'bg-teal':
                      !ticketingPage &&
                      (!reverseHeader || isPinned) &&
                      !navOpen,
                    'nav-hover': !reverseHeader || isPinned || ticketingPage,
                    'nav-hover-reverse':
                      reverseHeader && !isPinned && !ticketingPage,
                  },
                )}>
                <AnimatePresence exitBeforeEnter>
                  <motion.div
                    onClick={() => {
                      setSearchOpen((prev) => !prev);
                      setNavOpen(false);
                    }}
                    key={menuSwap}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}>
                    {!breakpoints.lg && searchOpen ? (
                      <Exit className='p-3 w-full h-full' />
                    ) : navOpen ? (
                      <SearchWhite className='p-3 lg:p-4 w-full h-full' />
                    ) : ticketingPage ? (
                      <SearchGrey className='p-3 lg:p-4 w-full h-full ' />
                    ) : reverseHeader && !isPinned ? (
                      <SearchWhite className='p-3 lg:p-4 w-full h-full' />
                    ) : (
                      <Search className='p-3 lg:p-4 w-full h-full' />
                    )}
                  </motion.div>
                </AnimatePresence>
              </button>
              <button
                onClick={() => {
                  toggleOpen();
                  setSearchOpen(false);
                }}
                className={cx(
                  'rounded-full w-12 lg:w-16 h-12 lg:h-16 bg-opacity-10 hover:bg-opacity-100 transition-standard ml-2',
                  {
                    '!bg-grey/10': ticketingPage,
                    'bg-white':
                      (reverseHeader && !isPinned) || navOpen || searchOpen,
                    'bg-teal':
                      !ticketingPage &&
                      (!reverseHeader || isPinned) &&
                      !navOpen,
                    'nav-hover': !reverseHeader || isPinned || ticketingPage,
                    'nav-hover-reverse':
                      reverseHeader && !isPinned && !ticketingPage,
                  },
                )}>
                <AnimatePresence exitBeforeEnter>
                  <motion.div
                    key={navOpen}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}>
                    {navOpen ? (
                      <Exit className='p-3 w-full h-full' />
                    ) : ticketingPage ? (
                      <MenuGrey className='p-3 lg:p-4 w-full h-full ' />
                    ) : (reverseHeader && !isPinned) || searchOpen ? (
                      <MenuWhite className='p-3 lg:p-4 w-full h-full' />
                    ) : (
                      <Menu className='p-3 lg:p-4 w-full h-full' />
                    )}
                  </motion.div>
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>
      </Headroom>

      {/* Side Menu & Nav Modal */}
      <AnimatePresence exitBeforeEnter>
        {sideMenuOpen && (
          <SideMenu toggleOpen={toggleOpen} open={sideMenuOpen} />
        )}
        {navOpen && <NavModal toggleOpen={toggleOpen} open={navOpen} />}
        {searchOpen && (
          <SearchModal setSearchOpen={setSearchOpen} searchOpen={searchOpen} />
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
